import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon, ClipboardPlusIcon } from 'lucide-react';
import { useState } from 'react';
import { FieldMap } from '@/components/map';
import { QuickActions } from '@/components/quick-actions';
import { EmptyState, ListSkeleton } from '@/components/states';
import { Badge, CollectibilityBadge, DpdBadge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBody, DataRow, SectionTitle } from '@/components/ui/card';
import { dateId, dateTimeId, orDash, rupiah } from '@/lib/format';
import { cn } from '@/lib/utils';
import { loanDetail } from '@/offline/queries';
import { useOfflineQuery } from '@/offline/use-offline-query';

export const Route = createFileRoute('/_app/nasabah/$loanId')({
  component: CustomerDetailPage,
});

type Tab = 'identitas' | 'pinjaman' | 'riwayat';

const VISIT_RESULT_LABELS: Record<string, string> = {
  BAYAR: 'Bayar',
  JANJI_BAYAR: 'Janji bayar',
  TIDAK_BERTEMU: 'Tidak bertemu',
  PINDAH_ALAMAT: 'Pindah alamat',
  MENOLAK_MEMBAYAR: 'Menolak membayar',
};

/**
 * Detail nasabah offline (T-P7-14, FR-DETAIL-01..09).
 *
 * Dibagi tab supaya layar ponsel tidak menjadi gulungan panjang: yang dibutuhkan
 * saat berdiri di depan rumah adalah identitas dan aksi cepat, sedangkan riwayat
 * dibuka ketika nasabah membantah pernah membayar.
 */
function CustomerDetailPage() {
  const { loanId } = Route.useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('identitas');

  const { data, loading } = useOfflineQuery(() => loanDetail(loanId), [loanId]);

  if (loading) return <ListSkeleton rows={4} />;

  if (!data) {
    return (
      <EmptyState
        title="Pinjaman tidak ada di perangkat ini"
        description="Data mungkin sudah dipindahkan ke collector lain, atau belum ikut tersinkron."
        action={
          <Link to="/nasabah" className="text-sm text-brand underline underline-offset-2">
            Kembali ke daftar
          </Link>
        }
      />
    );
  }

  const primaryAddress = data.addresses[0] ?? null;

  return (
    <div className="space-y-4 px-4 py-4">
      <header className="space-y-2">
        <Link
          to="/nasabah"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ArrowLeftIcon className="size-4" aria-hidden="true" />
          Daftar nasabah
        </Link>

        <div>
          <h1 className="text-lg font-semibold tracking-tight">{data.customer.fullName}</h1>
          <p className="text-sm text-muted-foreground">
            {data.loan.loanNumber} · CIF {data.customer.cif}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <CollectibilityBadge value={data.loan.collectibility} />
          <DpdBadge days={data.loan.dpd} />
          <Badge>{data.loan.status}</Badge>
        </div>
      </header>

      <QuickActions
        target={{
          phonePrimary: data.customer.phonePrimary,
          phoneAlt: data.customer.phoneAlt,
          latitude: primaryAddress?.row.latitude ?? null,
          longitude: primaryAddress?.row.longitude ?? null,
          addressText: primaryAddress?.line1 ?? null,
          customerName: data.customer.fullName,
        }}
      />

      <Button
        size="lg"
        fullWidth
        leading={<ClipboardPlusIcon className="size-5" aria-hidden="true" />}
        onClick={() => void navigate({ to: '/kunjungan/$loanId', params: { loanId } })}
      >
        Mulai kunjungan
      </Button>

      <div
        role="tablist"
        aria-label="Bagian detail"
        className="grid grid-cols-3 gap-1 rounded-card bg-muted p-1"
      >
        {(['identitas', 'pinjaman', 'riwayat'] as Tab[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              'rounded-[0.5rem] px-2 py-2 text-sm font-medium capitalize',
              tab === key ? 'bg-surface text-foreground shadow-sm' : 'text-muted-foreground',
            )}
          >
            {key}
          </button>
        ))}
      </div>

      {tab === 'identitas' ? (
        <section className="space-y-3">
          <Card>
            <CardBody>
              <dl className="divide-y divide-border">
                <DataRow label="Nama" value={data.customer.fullName} />
                <DataRow
                  label="NIK"
                  value={data.customer.nikLast4 ? `••••••••••••${data.customer.nikLast4}` : '—'}
                />
                <DataRow
                  label="Tempat, tanggal lahir"
                  value={`${orDash(data.customer.birthPlace)}, ${dateId(data.customer.birthDate)}`}
                />
                <DataRow label="Jenis kelamin" value={orDash(data.customer.gender)} />
                <DataRow label="Pekerjaan" value={orDash(data.customer.occupation)} />
                <DataRow label="Telepon" value={orDash(data.customer.phonePrimary)} />
                <DataRow label="Telepon alternatif" value={orDash(data.customer.phoneAlt)} />
              </dl>
            </CardBody>
          </Card>

          <SectionTitle>Alamat</SectionTitle>
          {data.addresses.map((address) => (
            <Card key={address.row.id}>
              <CardBody className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge tone={address.row.isPrimary ? 'brand' : 'neutral'}>
                    {address.row.type}
                  </Badge>
                  {address.row.latitude === null ? (
                    <Badge tone="warning">Tanpa koordinat</Badge>
                  ) : null}
                </div>
                <p className="text-sm">{address.line1}</p>
                <p className="text-xs text-muted-foreground">
                  {[
                    address.row.rtRw && `RT/RW ${address.row.rtRw}`,
                    address.row.kelurahan,
                    address.row.kecamatan,
                    address.row.city,
                    address.row.postalCode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </CardBody>
            </Card>
          ))}

          {primaryAddress?.row.latitude != null && primaryAddress.row.longitude != null ? (
            <FieldMap
              height={200}
              zoom={16}
              center={{ lat: primaryAddress.row.latitude, lng: primaryAddress.row.longitude }}
              markers={[
                {
                  id: data.customer.id,
                  lat: primaryAddress.row.latitude,
                  lng: primaryAddress.row.longitude,
                  title: data.customer.fullName,
                  subtitle: primaryAddress.line1,
                  collectibility: data.loan.collectibility,
                },
              ]}
            />
          ) : null}

          {data.contacts.length > 0 ? (
            <>
              <SectionTitle>Kontak lain</SectionTitle>
              {data.contacts.map((contact) => (
                <Card key={contact.row.id}>
                  <CardBody className="space-y-0.5">
                    <p className="text-sm font-medium">{contact.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {contact.row.relation} · {orDash(contact.phone)}
                    </p>
                  </CardBody>
                </Card>
              ))}
            </>
          ) : null}
        </section>
      ) : null}

      {tab === 'pinjaman' ? (
        <section className="space-y-3">
          <Card>
            <CardBody>
              <dl className="divide-y divide-border">
                <DataRow label="Produk" value={orDash(data.loan.productName)} />
                <DataRow label="Plafon" value={rupiah(data.loan.plafon)} />
                <DataRow label="Outstanding" value={rupiah(data.loan.outstanding)} />
                <DataRow label="Angsuran" value={rupiah(data.loan.installmentAmount)} />
                <DataRow label="Tunggakan pokok" value={rupiah(data.loan.arrearsPrincipal)} />
                <DataRow label="Tunggakan bunga" value={rupiah(data.loan.arrearsInterest)} />
                <DataRow label="Denda" value={rupiah(data.loan.penalty)} />
                <DataRow label="Jatuh tempo berikutnya" value={dateId(data.loan.nextDueDate)} />
                <DataRow label="Tunggakan tertua" value={dateId(data.loan.oldestUnpaidDueDate)} />
              </dl>
            </CardBody>
          </Card>

          <SectionTitle>Agunan</SectionTitle>
          {data.collaterals.length === 0 ? (
            <EmptyState title="Tidak ada agunan tercatat" />
          ) : (
            data.collaterals.map((collateral) => (
              <Card key={collateral.id}>
                <CardBody className="space-y-1">
                  <p className="text-sm font-medium">{collateral.type}</p>
                  <p className="text-xs text-muted-foreground">{orDash(collateral.description)}</p>
                  <p className="text-sm tabular-nums">
                    {collateral.appraisedValue ? rupiah(collateral.appraisedValue) : '—'}
                  </p>
                </CardBody>
              </Card>
            ))
          )}
        </section>
      ) : null}

      {tab === 'riwayat' ? (
        <section className="space-y-3">
          <SectionTitle>Riwayat pembayaran</SectionTitle>
          {data.payments.length === 0 ? (
            <EmptyState title="Belum ada pembayaran tercatat" />
          ) : (
            data.payments.slice(0, 20).map((payment) => (
              <Card key={payment.id}>
                <CardBody className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{dateId(payment.paidAt)}</p>
                    <p className="text-xs text-muted-foreground">
                      {orDash(payment.channel)} · {payment.externalRef}
                    </p>
                  </div>
                  <p className="shrink-0 text-sm font-semibold tabular-nums">
                    {rupiah(payment.amount)}
                  </p>
                </CardBody>
              </Card>
            ))
          )}

          <SectionTitle>Riwayat kunjungan</SectionTitle>
          {data.visits.length === 0 ? (
            <EmptyState title="Belum ada kunjungan tercatat" />
          ) : (
            data.visits.slice(0, 20).map((visit) => (
              <Card key={visit.id}>
                <CardBody className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">
                      {VISIT_RESULT_LABELS[visit.result] ?? visit.result}
                    </p>
                    {visit.pending ? <Badge tone="warning">Belum terkirim</Badge> : null}
                  </div>
                  <p className="text-xs text-muted-foreground">{dateTimeId(visit.checkinAt)}</p>
                  {visit.notes ? <p className="text-sm">{visit.notes}</p> : null}
                  {visit.isFlagged ? (
                    <p className="text-xs text-danger">{orDash(visit.flagReason)}</p>
                  ) : null}
                </CardBody>
              </Card>
            ))
          )}
        </section>
      ) : null}
    </div>
  );
}
